import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { Subject } from 'rxjs';

// The levels of the server's tax register (PRRO) log.
export type PrroLogLevel = 'Information' | 'Warning' | 'Error';

export const PRRO_LOG_LEVELS: readonly PrroLogLevel[] = [
  'Information',
  'Warning',
  'Error',
];

// A tax register (PRRO) log entry as Nt.Pos.Api takes it (POST prro/logs), with what the device
// keeps of it: the tenant it was logged in, whose request sends it, and its size.
export interface PrroLogEntry {
  id: string;
  timestamp: string;
  level: PrroLogLevel;
  taxRegisterFiscalNumber?: string;
  tin?: string;
  ipn?: string;
  operation?: string;
  message: string;
  properties?: Record<string, unknown>;
  tenantId?: string;
  // Characters of the message and the properties.
  size?: number;
}

// Characters kept, within the server's limits (8000 of the message, 30000 of the properties).
const MESSAGE_LENGTH = 8000;
const VALUE_LENGTH = 10000;
const PROPERTIES_LENGTH = 20000;
// Entries kept while nothing takes them (PrroLogService not started).
const MAX_ENTRIES = 1000;
// The properties the logger's callers put a register's fiscal number in.
const REGISTER_PROPERTIES = [
  'taxRegisterFiscalNumber',
  'taxRegister',
  'taxRegisterFiscalNum',
];

// The tax register logger's entries on their way to the server: MonitoringService adds them,
// PrroLogService takes them. It depends on nothing, so the logger adds no dependency cycle.
@Injectable({ providedIn: 'root' })
export class PrroLogQueue {
  // Emits after an entry is added.
  readonly added = new Subject<void>();
  private _entries: PrroLogEntry[] = [];

  add(entry: PrroLogEntry): void {
    this._entries.push(entry);
    if (this._entries.length > MAX_ENTRIES) {
      this._entries.splice(0, this._entries.length - MAX_ENTRIES);
    }
    this.added.next();
  }

  // The entries added since the last call.
  take(): PrroLogEntry[] {
    const entries = this._entries;
    this._entries = [];
    return entries;
  }
}

// An entry of the tax register logger, called with a message or an error and its properties:
// the register and the operation move out of the properties into their own fields, an error's
// details into the `error` property, and the rest is kept as JSON.
export function createPrroLogEntry(
  level: PrroLogLevel,
  value: unknown,
  properties?: Record<string, unknown> | null,
  tenantId?: string | null,
  now = new Date(),
  createId: () => string = () => UUID.UUID(),
): PrroLogEntry {
  const rest: Record<string, unknown> = { ...(properties || {}) };

  let taxRegisterFiscalNumber: string | undefined;
  for (const key of REGISTER_PROPERTIES) {
    const fiscalNumber = toFiscalNumber(rest[key]);
    if (fiscalNumber) {
      taxRegisterFiscalNumber ??= fiscalNumber;
      delete rest[key];
    }
  }

  let operation: string | undefined;
  if (typeof rest.operation === 'string' && rest.operation.trim()) {
    operation = rest.operation.trim();
    delete rest.operation;
  }

  if (value !== null && typeof value === 'object' && rest.error === undefined) {
    rest.error =
      value instanceof Error
        ? { name: value.name, stack: value.stack }
        : value;
  }

  const message = truncate(toLogMessage(value), MESSAGE_LENGTH);
  const json = toJson(rest);
  const kept =
    json === '{}'
      ? undefined
      : json.length > PROPERTIES_LENGTH
        ? { truncated: json.slice(0, PROPERTIES_LENGTH) }
        : (JSON.parse(json) as Record<string, unknown>);

  return {
    id: createId(),
    timestamp: now.toISOString(),
    level,
    taxRegisterFiscalNumber,
    operation,
    message,
    properties: kept,
    tenantId: tenantId || undefined,
    size: message.length + (kept ? Math.min(json.length, PROPERTIES_LENGTH) : 0),
  };
}

// The readable text of what the logger was called with: a message, an error, an HTTP error or
// a result of the tax API.
export function toLogMessage(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return value.message || value.name;
  }
  if (typeof value === 'object') {
    const details = value as Record<string, unknown>;
    for (const key of ['message', 'ResultText', 'text', 'error', 'status']) {
      const text = details[key];
      if (typeof text === 'string' && text.trim()) {
        return text;
      }
    }
    if (details.error && typeof details.error === 'object') {
      return toLogMessage(details.error);
    }
    return toJson(value);
  }
  return String(value);
}

// JSON of anything: errors with their message and stack, long strings cut, cycles and
// HTTP headers left out.
export function toJson(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    return (
      JSON.stringify(value, (key, item) => {
        if (key === 'headers') {
          return undefined;
        }
        if (typeof item === 'string') {
          return truncate(item, VALUE_LENGTH);
        }
        if (typeof item === 'bigint') {
          return item.toString();
        }
        if (item instanceof Error) {
          return { name: item.name, message: item.message, stack: item.stack };
        }
        if (item !== null && typeof item === 'object') {
          if (seen.has(item)) {
            return '[Circular]';
          }
          seen.add(item);
        }
        return item;
      }) ?? 'null'
    );
  } catch {
    return JSON.stringify(String(value));
  }
}

function toFiscalNumber(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim() || undefined;
  }
  if (value !== null && typeof value === 'object') {
    return toFiscalNumber((value as { fiscalNumber?: unknown }).fiscalNumber);
  }
  return undefined;
}

function truncate(value: string, length: number): string {
  return value.length > length ? value.slice(0, length) : value;
}
